/**
 * Composable for PWA install functionality.
 * Handles the install prompt lifecycle, iOS instructions, installation detection, and analytics tracking.
 *
 * Note: The dynamic PWA manifest is injected on page load by entry points (OTPPage.vue,
 * CreditAppCreateApplication.vue) with correct route params. This composable handles
 * the install prompt UI and state management.
 *
 * @example
 * ```vue
 * const {
 *   isInstallable,
 *   isInstalled,
 *   shouldShowInstallGuide,
 *   promptInstall,
 * } = usePwaInstall({
 *   language: computed(() => customer.language),
 *   customerId: computed(() => customer?.id),
 *   onShouldShowGuide: () => showInstallBanner.value = true,
 * });
 * ```
 */
import { ref, unref, onMounted, onBeforeUnmount, computed, type MaybeRef } from 'vue';
import util from '@core/services/util';
import LOCALIZATION from '@core/classes/Localization';

export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable' | 'ios' | 'already_installed';

export interface UsePwaInstallOptions {
	language?: MaybeRef<'English' | 'Spanish'>;
	customerId?: MaybeRef<string | undefined>;
	/** Called when the install prompt becomes available */
	onInstallAvailable?: () => void;
	/** Called when the app is successfully installed */
	onInstalled?: () => void;
	/** Called when we detect the user should be shown an install guide (proactive prompt) */
	onShouldShowGuide?: () => void;
}

// Storage key for tracking user's dismissal of the install guide
const INSTALL_GUIDE_DISMISSED_KEY = 'pwa_install_guide_dismissed';
const INSTALL_GUIDE_DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Checks if the user has dismissed the install guide recently.
 */
function hasUserDismissedGuide(): boolean {
	try {
		const dismissedAt = localStorage.getItem(INSTALL_GUIDE_DISMISSED_KEY);
		if (!dismissedAt) return false;

		const dismissedTime = parseInt(dismissedAt, 10);
		const now = Date.now();

		// If dismissed within the duration, don't show again
		return now - dismissedTime < INSTALL_GUIDE_DISMISS_DURATION_MS;
	} catch {
		return false;
	}
}

/**
 * Records that the user dismissed the install guide.
 */
function recordGuideDismissal(): void {
	try {
		localStorage.setItem(INSTALL_GUIDE_DISMISSED_KEY, Date.now().toString());
	} catch {
		// localStorage may be unavailable
	}
}

/**
 * Checks if the PWA is installed using the getInstalledRelatedApps API.
 * This API is supported on Chrome Android 84+ and Chrome Desktop 140+.
 * @see https://developer.chrome.com/docs/capabilities/get-installed-related-apps
 */
async function checkInstalledViaApi(): Promise<boolean> {
	try {
		// Feature detection with optional chaining
		if (!('getInstalledRelatedApps' in navigator)) {
			return false;
		}

		const relatedApps = await (navigator as Navigator & {
			getInstalledRelatedApps: () => Promise<Array<{ platform: string; url?: string; id?: string }>>;
		}).getInstalledRelatedApps();

		return relatedApps.length > 0;
	} catch {
		// API call failed - assume not installed
		return false;
	}
}

export function usePwaInstall(options: UsePwaInstallOptions = {}) {
	// Reactive getters that handle both ref and plain values
	const language = computed(() => unref(options.language) ?? 'English');
	const customerId = computed(() => unref(options.customerId));

	const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
	const isInstallable = ref(false);
	const isInstalled = ref(false);
	const isPrompting = ref(false);
	const isCheckingInstallation = ref(true);
	const userDismissedGuide = ref(false);

	// Localization helper
	const getLocalized = (key: keyof typeof LOCALIZATION.customerPortalNavBar.pwaInstall, field: 'title' | 'message') => {
		return LOCALIZATION.customerPortalNavBar.pwaInstall?.[key]?.[field]?.[language.value] ?? '';
	};

	/**
	 * Determines if the install guide should be shown to the user.
	 * True when:
	 * - User is on a mobile device
	 * - App is NOT running as standalone (not launched from home screen)
	 * - App is NOT detected as installed
	 * - User hasn't dismissed the guide recently
	 * - Either: browser supports PWA install (Android) OR is iOS (manual install)
	 */
	const shouldShowInstallGuide = computed(() => {
		// Don't show while we're still checking installation status
		if (isCheckingInstallation.value) return false;

		// Don't show if already installed or running as standalone
		if (isInstalled.value) return false;

		// Don't show if user recently dismissed the guide
		if (userDismissedGuide.value) return false;

		// Only show on mobile devices
		if (!util.isMobileDevice()) return false;

		// Show if installable (Android with beforeinstallprompt) or iOS (manual install)
		return isInstallable.value || util.isIosDevice();
	});

	/**
	 * Call this when the user dismisses the install guide.
	 * This will prevent it from showing again for 7 days.
	 */
	const dismissInstallGuide = () => {
		userDismissedGuide.value = true;
		recordGuideDismissal();

		util.logRocketTrack('PWA_Install_Guide_Dismissed', {
			customerId: customerId.value,
		});
	};

	const handleBeforeInstallPrompt = (event: Event) => {
		const promptEvent = event as BeforeInstallPromptEvent;
		promptEvent.preventDefault();

		// Only capture prompt on mobile devices
		if (!util.isMobileDevice()) return;

		deferredPrompt.value = promptEvent;
		isInstallable.value = true;

		// If beforeinstallprompt fires, the app is definitely not installed
		// (browsers don't fire this event for already-installed PWAs)
		isInstalled.value = false;
		isCheckingInstallation.value = false;

		options.onInstallAvailable?.();

		// Check if we should proactively show the guide
		if (shouldShowInstallGuide.value) {
			options.onShouldShowGuide?.();
		}
	};

	const handleAppInstalled = () => {
		deferredPrompt.value = null;
		isInstallable.value = false;
		isInstalled.value = true;

		// Track successful installation
		util.logRocketTrack('PWA_Installed', {
			customerId: customerId.value,
		});

		util.toastr(
			'success',
			getLocalized('installed', 'title'),
			getLocalized('installed', 'message')
		);

		options.onInstalled?.();
	};

	/**
	 * Triggers the PWA install flow.
	 * Returns the outcome of the install attempt.
	 * Note: This function no longer shows toasters - the PwaInstallGuide component handles UI.
	 */
	const promptInstall = async (): Promise<PwaInstallOutcome> => {
		// Guard against concurrent invocations (double-click protection)
		if (isPrompting.value) {
			return 'unavailable';
		}

		// Already running as standalone app
		if (util.isStandaloneApp()) {
			return 'already_installed';
		}

		// iOS requires manual instructions
		// Note: The dynamic manifest is already injected on page load by entry points
		// (OTPPage.vue, CreditAppCreateApplication.vue) with correct route params
		if (util.isIosDevice()) {
			return 'ios';
		}

		// No deferred prompt available
		if (!deferredPrompt.value) {
			return 'unavailable';
		}

		isPrompting.value = true;

		try {
			// Note: The dynamic manifest is already injected on page load by entry points
			// (OTPPage.vue, CreditAppCreateApplication.vue) with correct route params
			await deferredPrompt.value.prompt();
			const choice = await deferredPrompt.value.userChoice;

			// Track the user's choice
			util.logRocketTrack('PWA_Install_Choice', {
				customerId: customerId.value,
				outcome: choice.outcome,
			});

			return choice.outcome;
		} catch (error) {
			util.error(`PWA install prompt failed: ${error instanceof Error ? error.message : String(error)}`);
			return 'unavailable';
		} finally {
			deferredPrompt.value = null;
			isPrompting.value = false;
		}
	};

	onMounted(async () => {
		// Check if user previously dismissed the guide
		userDismissedGuide.value = hasUserDismissedGuide();

		// Check if already running as standalone (definitely installed)
		if (util.isStandaloneApp()) {
			isInstalled.value = true;
			isCheckingInstallation.value = false;
		} else {
			// Try to detect installation via getInstalledRelatedApps API
			const installedViaApi = await checkInstalledViaApi();
			if (installedViaApi) {
				isInstalled.value = true;
			}
			isCheckingInstallation.value = false;

			// If on iOS and not installed, trigger the proactive prompt
			if (util.isIosDevice() && !isInstalled.value && shouldShowInstallGuide.value) {
				options.onShouldShowGuide?.();
			}
		}

		// Listen for install prompt (Android/Chrome)
		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		window.addEventListener('appinstalled', handleAppInstalled);
	});

	onBeforeUnmount(() => {
		window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		window.removeEventListener('appinstalled', handleAppInstalled);
	});

	return {
		/** Whether the app can be installed via browser prompt (Android/Chrome) */
		isInstallable,
		/** Whether the app is already installed or running as standalone */
		isInstalled,
		/** Whether we're still checking if the app is installed */
		isCheckingInstallation,
		/** Whether the install guide should be shown (mobile, not installed, not dismissed) */
		shouldShowInstallGuide,
		/** Call to record that the user dismissed the install guide */
		dismissInstallGuide,
		/** Trigger the install prompt (or show iOS instructions) */
		promptInstall,
	};
}

export default usePwaInstall;
